/*
  The floating product card in blog articles (Olle 21 okt 2026: "när en länk är i vyn så ska det
  animeras in ett produktkort för den produkten"). A link to a product shows that product with Add;
  a link to a collection shows the collection with Shop. A bar along the bottom on phones, a card
  centred at the bottom on desktop.

  Markup: <tso-blog-float> in sections/main-article-overlay. Cards: sections/tso-blog-float-card,
  fetched with the Section Rendering API from the link's own product or collection URL, so price,
  currency and stock are the visitor's. That section prints nothing for a sold-out product or a
  collection with nothing to buy, and such links are skipped. Links inside the article's own product
  cards (.tso-blog-cards) don't count.

  Which link, worked out on every scrolled frame (second round, Olle: the first version waited for
  scrolling to stop, "uppdateras inte mid scroll"): the last link, in reading order, whose top has
  passed a line 70 % down the screen. Links on the same line count once, the first of them. Above the
  first link, once the reader has scrolled a third of a screen, the start card (Lowe 24 okt 2026: "ett
  relevant kort i början"; readers scroll 20–25 % of the page and the first link often came later or
  never): the article's brand (data-start, from its tags in sections/main-article-overlay) or else the
  first link that has a card. Cards are fetched 800 px ahead; one that isn't there yet shows as soon
  as it arrives. The card hides while the article text is off screen (comments, deals, footer) and
  while a drawer or modal is open (CSS). Close hides it for the rest of the page view.

  Styles: assets/tso-blog-float.css.
*/
(() => {
  if (customElements.get('tso-blog-float')) return;

  const TRIGGER = 0.7;
  const SAME_LINE = 8;
  const START = 1 / 3; // screens of scrolling before the start card, so it doesn't come with the age gate and cookie banner
  // /products/<h>, /collections/<h>, /collections/<c>/products/<h>, optionally after a market prefix (/de-de/…)
  const PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(products|collections)\/([^/?#]+)(?:\/products\/([^/?#]+))?\/?$/i;
  const bare = (host) => host.trim().toLowerCase().replace(/^www\./, '');

  class TsoBlogFloat extends HTMLElement {
    connectedCallback() {
      this.body = this.closest('article')?.querySelector('[itemprop="articleBody"]');
      this.panel = this.querySelector('[data-tso-bfloat-panel]');
      this.holder = this.querySelector('[data-tso-bfloat-holder]'); // not "slot": HTMLElement.slot is a built-in
      if (!this.body || !this.panel || !this.holder) return;

      this.links = []; // [link, key] in reading order; key = 'products/<handle>' | 'collections/<handle>'
      this.cards = new Map(); // key -> the card's HTML once fetched ('' = no card)
      this.loading = new Set();
      this.current = '';
      this.closed = false;
      this.bodyInView = false;

      this.collect();
      // The start card's candidates: the brand, then the links in reading order
      this.starts = [...new Set([this.dataset.start, ...this.links.map(([, key]) => key)].filter(Boolean))];
      if (!this.starts.length) return;

      this.querySelector('[data-tso-bfloat-close]')?.addEventListener('click', () => this.close());
      this.observe();
    }

    disconnectedCallback() {
      this.observers?.forEach((observer) => observer.disconnect());
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      cancelAnimationFrame(this.frame);
    }

    // Links to this store: the page's own host, the shop's domains (data-hosts) and their www forms
    collect() {
      const hosts = (this.dataset.hosts || '').split(',').filter(Boolean).map(bare);
      hosts.push(bare(location.hostname));
      const collections = this.hasAttribute('data-collections');

      this.body.querySelectorAll('a[href]').forEach((link) => {
        if (link.closest('.tso-blog-cards')) return;
        let url;
        try {
          url = new URL(link.getAttribute('href'), location.href);
        } catch (error) {
          return;
        }
        if (!hosts.includes(bare(url.hostname))) return;
        const match = url.pathname.match(PATH);
        if (!match) return;

        const type = match[1].toLowerCase();
        const product = match[3] || (type === 'products' ? match[2] : '');
        if (product) this.links.push([link, `products/${product.toLowerCase()}`]);
        else if (collections) this.links.push([link, `collections/${match[2].toLowerCase()}`]);
      });
    }

    observe() {
      const prefetch = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          prefetch.unobserve(entry.target);
          this.load(entry.target.dataset.tsoBfloatKey);
        });
      }, { rootMargin: '800px 0px' });

      const article = new IntersectionObserver(([entry]) => {
        this.bodyInView = entry.isIntersecting;
        this.render();
      });

      this.links.forEach(([link, key]) => {
        link.dataset.tsoBfloatKey = key;
        prefetch.observe(link);
      });
      article.observe(this.body);
      this.observers = [prefetch, article];

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
    }

    update() {
      if (this.closed) return;
      const line = window.innerHeight * TRIGGER;
      let key = '';
      let keyTop = -Infinity;

      for (const [link, linkKey] of this.links) {
        const rect = link.getBoundingClientRect();
        const top = rect.top;
        if (!rect.height || top > line) continue; // not rendered (a closed FAQ answer), or not reached yet
        if (!this.cards.has(linkKey)) {
          this.load(linkKey);
          continue;
        }
        if (!this.cards.get(linkKey)) continue;
        if (Math.abs(top - keyTop) < SAME_LINE) continue;
        key = linkKey;
        keyTop = top;
      }

      if (!key) {
        const start = this.startKey(); // fetched from the start, shown after the first bit of scrolling
        if (window.scrollY > window.innerHeight * START) key = start;
      }

      if (key !== this.current) this.show(key);
    }

    // The first start candidate that has a card; they are fetched one at a time
    startKey() {
      for (const key of this.starts) {
        if (!this.cards.has(key)) {
          this.load(key);
          return '';
        }
        if (this.cards.get(key)) return key;
      }
      return '';
    }

    load(key) {
      if (this.cards.has(key) || this.loading.has(key)) return;
      this.loading.add(key);

      const params = new URLSearchParams({ section_id: this.dataset.section });
      if (key.startsWith('collections/')) params.set('sort_by', 'best-selling');
      if (window.Shopify?.country) params.set('country', window.Shopify.country);
      const root = window.Shopify?.routes?.root || '/';

      fetch(`${root}${key}?${params}`)
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => new DOMParser().parseFromString(text, 'text/html').querySelector('[data-tso-bfloat-item]')?.outerHTML || '')
        .catch(() => '')
        .then((html) => {
          this.cards.set(key, html);
          this.loading.delete(key);
          this.update();
        });
    }

    show(key) {
      this.current = key;
      if (key) {
        this.holder.innerHTML = this.cards.get(key); // the new card fades in (CSS)
        this.lift();
      }
      this.render();
    }

    // Klaviyo's "Get 10% Off" tab sits fixed at the bottom left; on phones the bar would cover it.
    // Its close button sticks out above the tab, hence the extra 14 px.
    lift() {
      let lift = 0;
      if (window.matchMedia('(max-width: 767px)').matches) {
        document.querySelectorAll('[class*="kl-teaser"]').forEach((teaser) => {
          const rect = teaser.getBoundingClientRect();
          if (rect.height && rect.bottom > window.innerHeight - 8) lift = Math.max(lift, window.innerHeight - rect.top + 14);
        });
      }
      this.style.setProperty('--tso-bfloat-lift', `${Math.round(lift)}px`);
    }

    render() {
      const on = Boolean(this.current) && !this.closed && this.bodyInView;
      this.panel.classList.toggle('is-visible', on);
      this.panel.inert = !on;
    }

    close() {
      this.closed = true;
      this.render();
    }
  }

  customElements.define('tso-blog-float', TsoBlogFloat);
})();
